import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import {Building2, Globe, Link2, CornerDownLeft, X} from 'lucide-react';
import {ConfirmProvider, useConfirm} from 'material-ui-confirm';
import {MuiChipsInput} from 'mui-chips-input';
import PropTypes from 'prop-types';
import React, {useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {z} from 'zod';
import {
  useCreateShareLinkMutation,
  useGetShareSettingsQuery,
  useUpdateShareLinkMutation,
} from '../../../data/documentWorkspaceApi';
import {useGetOrgUsersQuery} from '../../../data/api';
import {
  DARK_TEXT_COLOR,
  GREEN_BUTTON,
  GREY_SUBTLE,
  WHITE_100,
} from '../../../theme';
import OrgUsersModal from '../../common/OrgUsersModal';
import PulseDot from '../../common/PulseDot';
import {Modal} from '../../Modal';
import {DocumentAccessRole} from './constants';
import FailedInvitesDialog from './FailedInvitesDialog';
import OverflowTooltip from '../../common/OverflowTooltip';
import SelectRole from './SelectRole';
import {pluralizeList} from '../../../util/string';
import {selectEffectiveOrg} from '../../../data/session';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {trackProductEvent} from '../../../util';
import {getBaseUserProperties} from '../../../util/tracking';

const emailSchema = z.email();

const ALLOWED_DOCUMENT_ACCESS_ROLES = [
  DocumentAccessRole.Viewer,
  DocumentAccessRole.Editor,
];

const StyledMuiChipsInput = styled(MuiChipsInput)({
  borderRadius: '0.75rem',
  border: 0,
  '& .MuiOutlinedInput-notchedOutline': {
    border: 0,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.5rem',
    border: 0,
    '& .MuiInputBase-input': {
      paddingBlock: 0,
      border: 0,
    },
  },
  '& .MuiFormHelperText-root.Mui-error': {
    whiteSpace: 'nowrap',
    position: 'absolute',
    top: '100%',
  },
});

const styles = {
  modal: {
    base: {
      borderRadius: '0.5rem',
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
      backgroundColor: WHITE_100,
      minHeight: '220px',
    },
    container: {
      paddingBlock: '1.5rem',
    },
    formControl: {
      paddingInline: '1.5rem',
      label: {
        fontSize: '14px',
        fontWeight: '600',
        color: DARK_TEXT_COLOR,
        marginBottom: '8px',
      },
    },
    title: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      color: '#162523',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '150%',
      letterSpacing: '-0.14px',
    },
    chip: {
      borderRadius: '0.25rem',
      maxWidth: '200px',
      fontWeight: '500',
      fontSize: '0.75rem',
      paddingRight: '0.25rem',
      paddingBlock: 0,
      height: 'auto',
      // For specificity
      '&.MuiChip-root': {
        backgroundColor: '#F2F4F4',
      },
    },
    divider: {
      marginBlock: '0.5rem',
    },
  },
};

const ShareDocumentModalBody = ({documentId, isPdfReviewDoc = false}) => {
  const confirm = useConfirm();
  const {parentOrg, hasParentOrg, currentUser} = useSelector(
    (state) => state.session
  );
  const effectiveOrg = useSelector(selectEffectiveOrg);
  const {orgName} = effectiveOrg;
  const {email: currentUserEmail} = currentUser;
  const {externalDocumentSharing} = useFlags();

  const theme = useTheme();

  const userListRef = useRef(null);
  const chipsInputRef = useRef(null);

  const [inputIsEmpty, setInputIsEmpty] = useState(true);
  const [transientEmails, setTransientEmails] = useState([]);
  const [transientPermission, setTransientPermission] = useState(
    DocumentAccessRole.Viewer
  );
  const [entryUpdating, setEntryUpdating] = useState({});
  const [entryHighlighted, setEntryHighlighted] = useState({});
  const [validationError, setValidationError] = useState(null);
  const {data} = useGetOrgUsersQuery({orgId: effectiveOrg.orgId});
  const {users: orgUsers = []} = data || {};

  const handleEntryHighlight = (email) => {
    setEntryHighlighted((prev) => ({...prev, [email]: true}));
    setTimeout(() => {
      setEntryHighlighted((prev) => ({...prev, [email]: false}));
    }, 2000);
  };

  const handleEntryUpdating = (email, updating) => {
    setEntryUpdating((prev) => ({...prev, [email]: updating}));
  };

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
  });
  const [failureDialogOpen, setFailureDialogOpen] = useState(false);
  const [failedDetails, setFailedDetails] = useState([]);
  const [orgUsersModalOpen, setOrgUsersModalOpen] = useState(false);

  const parentOrgId = hasParentOrg && parentOrg?.orgId;

  const [createShareLink, {isLoading: isCreatingShareLink}] =
    useCreateShareLinkMutation();
  const [updateShareLink, {isLoading: isUpdatingShareLink}] =
    useUpdateShareLinkMutation();

  const {
    data: shareSettings,
    isLoading: isLoadingShareSettings,
    isError: isErrorShareSettings,
    refetch: refetchShareSettings,
  } = useGetShareSettingsQuery({documentId});

  const hasInputEmails = transientEmails.length > 0;
  const hasPersistedEmails = shareSettings?.sharedEmails?.length > 0;

  const parentOrgName = shareSettings?.parentOrgName ?? parentOrg?.orgName;
  const parentOrgRequiredRoles = shareSettings?.parentOrgRequiredRoles;

  const prepareShareLinkParams = () => {
    const linkParams = {
      documentId,
      orgId: effectiveOrg.orgId,
      grantedPermission: 'editor',
    };
    if (parentOrgId) {
      linkParams.parentOrgId = parentOrg.orgId;
      linkParams.parentOrgName = parentOrg.orgName;
      linkParams.parentOrgRequiredRoles = ['DPS', 'RPGS'];
      linkParams.parentOrgRequiredRolePermissionMapping = {
        DPS: 'editor',
        RPGS: 'viewer',
      };
    }

    return linkParams;
  };

  const handleShareLink = async () => {
    if (!documentId || !effectiveOrg?.orgId) {
      setSnackbarState({
        open: true,
        message: 'Unable to determine organization',
      });
      return;
    }

    try {
      await createShareLink(prepareShareLinkParams()).unwrap();

      // Build the share URL with ?shared=true query parameter
      const shareUrl = isPdfReviewDoc
        ? `${window.location.origin}/documents/pdf-review/${documentId}?shared=true`
        : `${window.location.origin}/documents/${documentId}?shared=true`;
      await navigator.clipboard.writeText(shareUrl);

      // Track share event
      trackProductEvent('User Shared Document', {
        ...getBaseUserProperties(currentUser, effectiveOrg),
        document_id: documentId,
        share_type: 'link',
        permission_level: 'editor',
      });

      setSnackbarState({
        open: true,
        message: 'Share link copied to clipboard!',
      });
    } catch (error) {
      console.error('Error creating share link:', error);
      setSnackbarState({
        open: true,
        message: 'Failed to create share link. Please try again.',
      });
    }
  };

  // Add user with default permissions if valid
  const handleSendInvites = async () => {
    if (!hasInputEmails) return;

    const shareLinkParams = prepareShareLinkParams();

    // Create the array with correct permission from activeRole
    const usersWithActiveRole = transientEmails.map((email) => ({
      email,
      permission: transientPermission,
      invitedBy: currentUserEmail,
      invitedAt: new Date().toISOString(),
    }));

    // Persist the updated sharedEmails list to backend
    try {
      const response = await updateShareLink({
        documentId,
        data: {
          ...shareLinkParams,
          sharedEmails: [
            ...(shareSettings?.sharedEmails ?? []),
            ...usersWithActiveRole,
          ],
        },
      }).unwrap();

      const {failedInvitations = []} = response;

      // Determine successes and failures
      const failedEmails = new Set(
        failedInvitations.map((inv) => inv.invite.email)
      );
      const successfulInvites = usersWithActiveRole.filter(
        (u) => !failedEmails.has(u.email)
      );

      // Parse out errors for dialog
      const parsedFailedDetails = failedInvitations.map(({invite, error}) => {
        let messages = [];
        try {
          const parsed = JSON.parse(error);
          Object.values(parsed).forEach((arr) =>
            messages.push(...(Array.isArray(arr) ? arr : [arr]))
          );
        } catch {
          messages = ['Unknown error'];
        }
        return {email: invite.email, messages};
      });

      // Store failed details for dialog
      if (parsedFailedDetails.length > 0) {
        setFailedDetails(parsedFailedDetails);
      }

      // Track share event for email invites
      trackProductEvent('User Shared Document', {
        ...getBaseUserProperties(currentUser, effectiveOrg),
        document_id: documentId,
        share_type: 'email',
        permission_level: transientPermission,
        recipient_count: usersWithActiveRole.length,
        successful_invites: successfulInvites.length,
        failed_invites: failedInvitations.length,
      });

      // Helper for failure messages with "Learn more" button
      const createFailureMessage = (text) => (
        <span>
          {text}
          <Button
            variant="text"
            size="small"
            sx={{
              ml: 1,
              textTransform: 'none',
              fontWeight: 500,
              color: GREEN_BUTTON,
              minWidth: 0,
              paddingLeft: '0.25rem',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setFailureDialogOpen(true);
            }}
          >
            Learn more
          </Button>
        </span>
      );

      // Compose snackbar message
      let message = null;
      let autoHideDuration = 3000;
      const failCount = failedInvitations.length;
      const successCount = successfulInvites.length;

      if (failCount && successCount) {
        const successText = `Invitations sent to ${successCount} user${successCount > 1 ? 's' : ''}.`;
        const failText = `Unable to invite ${failCount} email${failCount > 1 ? 's' : ''}.`;
        message = createFailureMessage(
          <>
            {successText}
            <br />
            {failText}
          </>
        );
      } else if (failCount) {
        message = createFailureMessage(
          `Unable to invite ${failCount} email${failCount > 1 ? 's' : ''}.`
        );
        autoHideDuration = 10000;
      } else if (successCount === 1) {
        message = `User "${successfulInvites[0].email}" added successfully.`;
      } else if (successCount === 2) {
        message = `Users "${successfulInvites[0].email}" and "${successfulInvites[1].email}" added successfully.`;
      } else if (successCount > 2) {
        message = `Invitations sent to ${successCount} users.`;
      }

      setSnackbarState({
        open: true,
        message,
        autoHideDuration,
      });

      // Clear emailsWithAccess after successful invite
      setTransientEmails([]);
      setTransientPermission(DocumentAccessRole.Viewer);
      refetchShareSettings();
    } catch (error) {
      console.error(error);

      setSnackbarState({
        open: true,
        message: 'Failed to send invites. Please try again.',
      });
    }
  };

  // Handle remove user
  const handleRemoveUser = async (email) => {
    const shareLinkParams = prepareShareLinkParams();
    const {confirmed} = await confirm({
      title: 'Remove user',
      content: (
        <Typography variant="body2">
          Are you sure you want to remove access for{' '}
          <code style={{fontWeight: 600, color: DARK_TEXT_COLOR}}>{email}</code>{' '}
          from this document?
        </Typography>
      ),
      confirmationText: 'Remove',
      cancelText: 'Cancel',
      cancellationButtonProps: {
        sx: {
          ...theme.button.secondary.medium,
        },
      },
      confirmationButtonProps: {
        sx: {
          ...theme.button.primary.medium,
        },
      },
      titleProps: {
        sx: {
          fontSize: '16px',
          fontWeight: 600,
          color: DARK_TEXT_COLOR,
        },
      },
      contentProps: {
        sx: {
          fontSize: '14px',
          fontWeight: 500,
          color: DARK_TEXT_COLOR,
        },
      },
    });

    if (!confirmed) return;

    setEntryUpdating((prev) => ({...prev, [email]: true}));
    try {
      await updateShareLink({
        documentId,
        data: {
          ...shareLinkParams,
          sharedEmails: shareSettings?.sharedEmails?.filter(
            (item) => item.email.toLowerCase() !== email.toLowerCase()
          ),
        },
      }).unwrap();

      await refetchShareSettings();

      setSnackbarState({
        open: true,
        message: `Access for user "${email}" removed successfully`,
      });
    } catch (error) {
      console.error(error);
      setSnackbarState({
        open: true,
        message: 'Failed to remove user. Please try again.',
      });
    } finally {
      setEntryUpdating((prev) => ({...prev, [email]: false}));
    }
  };

  // Update permission for a specific user
  const handlePermissionChange = async (email, newPermission) => {
    handleEntryUpdating(email, true);

    const shareLinkParams = prepareShareLinkParams();
    try {
      await updateShareLink({
        documentId,
        data: {
          ...shareLinkParams,
          sharedEmails: shareSettings?.sharedEmails?.map((item) =>
            item.email.toLowerCase() === email.toLowerCase()
              ? {...item, permission: newPermission}
              : item
          ),
        },
      }).unwrap();

      await refetchShareSettings();

      setSnackbarState({
        open: true,
        message: `Permission for user "${email}" updated successfully`,
      });
    } catch (error) {
      console.error(error);
      setSnackbarState({
        open: true,
        message: 'Failed to update permission. Please try again.',
      });
    } finally {
      handleEntryUpdating(email, false);
    }
  };

  // const [regenerateShareLink, {isLoading: isRegeneratingShareLink}] =
  //   useRegenerateShareLinkMutation();

  // const copyIndividualShareLink = async (email) => {
  //   try {
  //     const response = await regenerateShareLink({
  //       documentId,
  //       emails: [email],
  //     }).unwrap();
  //     // Smarter check: look for email in failedEmails array first
  //     const failedEmails = response?.failedEmails || [];
  //     if (failedEmails.includes(email)) {
  //       setSnackbarState({
  //         open: true,
  //         message:
  //           'Failed to generate a share link for this user. Please try again.',
  //       });
  //     } else {
  //       const regeneratedLinks = response?.regeneratedLinks || [];
  //       const foundLinkObj = regeneratedLinks.find(
  //         (l) => l.email?.toLowerCase() === email.toLowerCase()
  //       );
  //       if (foundLinkObj && foundLinkObj.magicLink) {
  //         console.log('foundLinkObj', foundLinkObj.magicLink);
  //         await navigator.clipboard.writeText(foundLinkObj.magicLink);
  //         setSnackbarState({
  //           open: true,
  //           message: 'Share link copied to clipboard!',
  //         });
  //       } else {
  //         setSnackbarState({
  //           open: true,
  //           message: 'Failed to find a share link for this user.',
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     setSnackbarState({
  //       open: true,
  //       message: 'Failed to copy individual share link. Please try again.',
  //     });
  //   }
  // };

  // Validate chip input and manage error state
  // NOTE: We return `false` instead of `{isError, textError}` on validation failure.
  // Returning the error object would set MuiChipsInput's internal error state, which
  // persists until the next validation and can't be cleared on input change.
  // By returning `false`, we reject the chip without triggering internal error display,
  // and use our controlled `validationError` state + `error`/`helperText` props instead.
  const validateInput = (chipValue) => {
    if (!chipValue) {
      return null;
    }
    const trimmed = chipValue?.trim().toLowerCase();
    if (!trimmed) {
      return null;
    }
    if (!emailSchema.safeParse(trimmed).success) {
      setValidationError('Please enter a valid email address');
      return false;
    }
    if (
      transientEmails.some(
        (email) => email.toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      handleEntryHighlight(trimmed.toLowerCase());
      setValidationError(
        'This email has already been added to the invite list'
      );
      return false;
    }

    if (
      orgUsers.some(
        (user) => user.email?.toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      setValidationError(
        'User is already in your organization and cannot be invited'
      );
      return false;
    }

    if (Array.isArray(shareSettings?.sharedEmails)) {
      const exists = shareSettings.sharedEmails.some(
        (entry) => entry.email?.toLowerCase() === trimmed.toLowerCase()
      );
      if (exists) {
        // Find the index in the sharedEmails list
        const index = shareSettings.sharedEmails.findIndex(
          (entry) => entry.email?.toLowerCase() === trimmed.toLowerCase()
        );
        // Try to scroll to this user in the list by data-entry-key
        if (index !== -1) {
          const selector = `[data-entry-key="${shareSettings.sharedEmails[index].email?.toLowerCase()}"]`;
          const userElement = document.querySelector(selector);
          if (userElement) {
            userElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });

            handleEntryHighlight(trimmed);
          }
        }
        setValidationError('User already has access to this document');
        return false;
      }
    }

    // Clear previous failure details when adding new emails
    if (failedDetails.length > 0) {
      setFailedDetails([]);
    }

    return true;
  };

  const parentOrgRequiredRolesCount = parentOrgRequiredRoles?.length;

  return (
    <>
      <Stack direction="column" gap="1.25rem" sx={styles.modal.container}>
        {externalDocumentSharing && !isPdfReviewDoc && (
          <>
            <FormControl
              sx={{
                ...styles.modal.formControl,
                flex: 1,
                '& .MuiFormHelperText-root': {marginLeft: '0'},
              }}
            >
              <FormLabel htmlFor="email" sx={styles.modal.formControl.label}>
                Share with external users
              </FormLabel>
              <Stack direction="row" flex={1} gap="0.5rem">
                <Stack
                  direction="row"
                  flex={1}
                  gap="0.5rem"
                  sx={{
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: '0.5rem',
                    // Use the theme outline color when a child of this is focused
                    '&:focus-within': {
                      outline: `2px solid ${GREEN_BUTTON}`,
                      outlineOffset: -1,
                    },
                  }}
                >
                  <StyledMuiChipsInput
                    ref={chipsInputRef}
                    autoComplete="off"
                    disableDeleteOnBackspace
                    disableEdition
                    placeholder="Enter email press enter to add"
                    InputProps={{
                      endAdornment: !inputIsEmpty && (
                        <Button
                          variant="text"
                          sx={{
                            ...theme.button.text.small,
                            height: 'auto',
                            paddingInline: '0.25rem',
                            paddingBlock: '0',
                            alignItems: 'center',
                            gap: 0,
                            borderRadius: '4px',
                            color: GREY_SUBTLE,
                            '&:hover': {
                              ...theme.button.text.small['&:hover'],
                              color: DARK_TEXT_COLOR,
                            },
                          }}
                          size="small"
                          disableRipple
                          onClick={() => {
                            // Simulate the Enter key pressed in the input
                            if (chipsInputRef?.current) {
                              const event = new KeyboardEvent('keydown', {
                                key: 'Enter',
                                code: 'Enter',
                                which: 13,
                                keyCode: 13,
                                bubbles: true,
                              });
                              chipsInputRef.current.dispatchEvent(event);
                              // Optionally focus (bring cursor to input)
                              chipsInputRef.current.focus();
                            }
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              mr: 0.5,
                              paddingTop: '0.125rem',
                              fontSize: '0.7rem',
                              lineHeight: '1.25',
                            }}
                          >
                            Press enter to add
                          </Typography>
                          <CornerDownLeft size={14} strokeWidth={1.5} />
                        </Button>
                      ),
                    }}
                    fullWidth
                    hideClearAll
                    onInputChange={(value) => {
                      setInputIsEmpty(value.length === 0);
                      setValidationError(null);
                    }}
                    onChange={(value) => setTransientEmails(value)}
                    renderChip={(Component, key, props) => (
                      <Component
                        deleteIcon={
                          <X
                            color={DARK_TEXT_COLOR}
                            size={12}
                            strokeWidth={3}
                          />
                        }
                        key={key}
                        {...props}
                        sx={styles.modal.chip}
                      />
                    )}
                    sx={{
                      '& .MuiInputBase-root': {
                        paddingBlock:
                          transientEmails.length > 0 ? '0.375rem' : '0.5rem',
                      },
                    }}
                    validate={validateInput}
                    value={transientEmails}
                    error={!!validationError}
                    helperText={validationError}
                  />
                  <SelectRole
                    roles={ALLOWED_DOCUMENT_ACCESS_ROLES}
                    formControlSx={{
                      alignSelf: 'flex-end',
                      width: '130px',
                    }}
                    onChange={(event) =>
                      setTransientPermission(event.target.value)
                    }
                    selectSx={{
                      alignSelf: 'flex-end',
                      '& .MuiFormControl-root': {
                        alignSelf: 'flex-end',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        alignSelf: 'flex-end',
                        border: 'none',
                      },
                    }}
                    value={transientPermission}
                  />
                </Stack>
                <Button
                  disabled={
                    isUpdatingShareLink || !hasInputEmails || !inputIsEmpty
                  }
                  onClick={handleSendInvites}
                  sx={{
                    ...theme.button.primary.medium,
                    '&:focus': {
                      outline: '-webkit-focus-ring-color auto 1px',
                    },
                  }}
                >
                  Invite
                </Button>
              </Stack>
            </FormControl>
            {failedDetails.length !== 0 && (
              <Stack
                direction="row"
                justifyContent="space-between"
                style={{paddingInline: '1.5rem'}}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                    color: theme.palette.error.main,
                    marginTop: '-0.75rem',
                  }}
                >
                  Some invitations failed.{' '}
                  <Box
                    component="span"
                    onClick={() => setFailureDialogOpen(true)}
                    sx={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      color: GREEN_BUTTON,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    View details
                  </Box>
                </Typography>
              </Stack>
            )}
            {!isLoadingShareSettings && isErrorShareSettings && (
              <Typography color="error" variant="body2">
                Failed to load share settings. Please try again.
              </Typography>
            )}
            <Stack
              className="persist-scrollbar"
              ref={userListRef}
              direction="column"
              gap="0.5rem"
              sx={{
                maxHeight: '30vh',
                overflowY: 'auto',
                paddingBottom: hasPersistedEmails ? '0.5rem' : 0,
                paddingTop: hasPersistedEmails ? '0.75rem' : 0,
                paddingLeft: '1rem',
                paddingRight: '0.5rem',
                '@-moz-document url-prefix()': {
                  marginInline: '0.5rem',
                  paddingRight: '1rem',
                },
                marginRight: '0.5rem',
              }}
            >
              {isLoadingShareSettings ? (
                <Stack direction="column" gap="0.5rem">
                  {[...Array(2)].map((_, i) => (
                    <Skeleton
                      animation="wave"
                      height={36}
                      key={i}
                      variant="rounded"
                    />
                  ))}
                </Stack>
              ) : (
                shareSettings?.sharedEmails?.map(
                  ({email, permission, firstName, lastName}) => {
                    const displayName =
                      firstName && lastName
                        ? `${firstName} ${lastName}`
                        : email;
                    return (
                      <Stack
                        data-entry-key={email}
                        direction="row"
                        gap="0.125rem"
                        key={email}
                        sx={{
                          padding: '0.125rem 0 0',
                          flex: 1,
                          position: 'relative',
                        }}
                      >
                        <Stack
                          direction="row"
                          gap="0.125rem"
                          flex={1}
                          justifyContent="flex-start"
                        >
                          {entryHighlighted[email] && (
                            <PulseDot
                              color={theme.palette.green.main}
                              size={12}
                              style={{margin: '0.25rem'}}
                            />
                          )}
                          <Stack direction="column">
                            <OverflowTooltip
                              text={displayName}
                              passthroughProps={{
                                sx: {fontWeight: 600},
                              }}
                            />
                            <OverflowTooltip
                              text={email}
                              passthroughProps={{
                                color: displayName && 'text.secondary',
                                variant: 'subtitle2',
                                sx: {
                                  fontSize: displayName
                                    ? '0.75rem'
                                    : '0.875rem',
                                  fontWeight: 500,
                                },
                              }}
                            />
                          </Stack>
                        </Stack>
                        {entryUpdating[email] ? (
                          <Skeleton
                            height={36}
                            variant="rounded"
                            width={130}
                            sx={{
                              borderRadius: '0.5rem',
                            }}
                          />
                        ) : (
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap="0.25rem"
                          >
                            {/* <Tooltip title="Copy link to clipboard">
                          <span>
                            <Button
                              variant="text"
                              sx={{
                                ...theme.button.text.small,
                                padding: 0,
                                gap: 0,
                                minWidth: 0,
                                width: '37px',
                              }}
                              loading={isRegeneratingShareLink}
                              size="small"
                              onClick={() => copyIndividualShareLink(email)}
                            >
                              <Link2 size={16} strokeWidth={1.5} />
                            </Button>
                          </span>
                        </Tooltip> */}
                            <SelectRole
                              roles={ALLOWED_DOCUMENT_ACCESS_ROLES}
                              footerComponent={
                                <Button
                                  onClick={() => handleRemoveUser(email)}
                                  sx={theme.button.outline.medium}
                                >
                                  Remove
                                </Button>
                              }
                              onChange={(event) =>
                                handlePermissionChange(
                                  email,
                                  event.target.value
                                )
                              }
                              value={permission}
                            />
                          </Stack>
                        )}
                      </Stack>
                    );
                  }
                )
              )}
            </Stack>
            <Divider sx={{marginInline: '1.5rem'}} />
          </>
        )}
        <FormControl sx={styles.modal.formControl}>
          <Stack
            direction="row"
            gap="0.5rem"
            justifyContent="space-between"
            marginTop="0.5rem"
          >
            <Stack direction="row" flex={1} gap="0.5rem">
              <Box sx={{paddingTop: '0.125rem'}}>
                <Building2 size={24} strokeWidth={1.5} />
              </Box>
              <Stack direction="column" flex={1}>
                <Typography sx={{fontWeight: 600}}>{orgName}</Typography>
                <Typography
                  color="text.secondary"
                  sx={{fontWeight: 400, fontSize: '0.75rem'}}
                  variant="subtitle2"
                >
                  Anyone in this organization can access.
                </Typography>
              </Stack>
              <Button
                onClick={() => setOrgUsersModalOpen(true)}
                sx={theme.button.text.small}
              >
                View all users
              </Button>
            </Stack>
          </Stack>
        </FormControl>
        {parentOrgId && parentOrgRequiredRolesCount && (
          <FormControl sx={styles.modal.formControl}>
            <Stack
              direction="row"
              gap="0.5rem"
              justifyContent="space-between"
              marginTop="0.5rem"
            >
              <Stack direction="row" flex={1} gap="0.5rem">
                <Box sx={{paddingTop: '0.125rem'}}>
                  <Globe size={24} strokeWidth={1.5} />
                </Box>
                <Stack direction="column" flex={1}>
                  <Typography sx={{fontWeight: 600}}>
                    {parentOrgName}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{fontWeight: 400, fontSize: '0.75rem'}}
                    variant="subtitle2"
                  >
                    {parentOrgRequiredRoles.length === 1 ? (
                      <>
                        Role {pluralizeList(parentOrgRequiredRoles)} has
                        document level access.
                      </>
                    ) : (
                      <>
                        Roles {pluralizeList(parentOrgRequiredRoles)} have
                        document level access.
                      </>
                    )}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </FormControl>
        )}
      </Stack>
      <Divider sx={styles.modal.divider} />
      <Stack
        direction="row"
        justifyContent="flex-end"
        paddingBottom="0.75rem"
        paddingInline="1.5rem"
        paddingTop="0.25rem"
      >
        <Button
          disabled={isCreatingShareLink}
          onClick={handleShareLink}
          startIcon={<Link2 size={20} strokeWidth={1.5} />}
          sx={{
            ...theme.button.secondary.medium,
            gap: 0,
          }}
          variant="contained"
        >
          <Typography size="small" variant="body2">
            Copy Link
          </Typography>
        </Button>
      </Stack>
      <Snackbar
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        autoHideDuration={snackbarState.autoHideDuration || 3000}
        message={snackbarState.message}
        onClose={() => setSnackbarState({open: false, message: ''})}
        open={snackbarState.open}
        sx={{
          '& .MuiSnackbarContent-root': {
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      />
      <FailedInvitesDialog
        open={failureDialogOpen}
        onClose={() => setFailureDialogOpen(false)}
        failedDetails={failedDetails}
      />
      <OrgUsersModal
        open={orgUsersModalOpen}
        onClose={() => setOrgUsersModalOpen(false)}
        orgId={effectiveOrg.orgId}
        orgName={orgName}
      />
    </>
  );
};

ShareDocumentModalBody.propTypes = {
  documentId: PropTypes.string.isRequired,
  isPdfReviewDoc: PropTypes.bool,
};

const ShareDocumentModal = ({
  open,
  onClose,
  documentId,
  isPdfReviewDoc = false,
}) => {
  return (
    <ConfirmProvider>
      <Modal
        onClose={onClose}
        open={open}
        title="Share document"
        disableEscapeKeyDown
      >
        <ShareDocumentModalBody
          documentId={documentId}
          isPdfReviewDoc={isPdfReviewDoc}
        />
      </Modal>
    </ConfirmProvider>
  );
};

ShareDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  documentId: PropTypes.string,
  isPdfReviewDoc: PropTypes.bool,
};

export default ShareDocumentModal;
